/*
 * Vendored from React Bits (https://github.com/DavidHDev/react-bits), src/ts-default/Animations/ClickSpark/ClickSpark.tsx
 * Upstream commit e1bbb696fc53f7f91e694c529e4d68c899773b6e (2026-09-30). Licence: MIT + Commons Clause, see ../LICENSE.md.
 * Use only inside this product; do not redistribute these components as a kit or package.
 * Change: openspec/changes/add-subtle-portal-micro-interactions.
 * Local patches:
 *   1. Performance: the animation frame loop only runs while sparks are visible (upstream redraws forever).
 *   2. Sharp lines on high-density screens: the canvas is scaled by devicePixelRatio (max 2).
 *   3. Imperative burst(x?, y?) via ref, and sparkOnClick to switch off the click trigger, so a success
 *      moment can fire sparks without a click.
 *   4. sparkColor also accepts a list of colours (cycled per spark), plus className, style and lineWidth props.
 *   5. Memory: the canvas backing store is only allocated while sparks are drawn and released afterwards
 *      (a full-screen layer would otherwise hold a viewport-sized bitmap for the whole session).
 */
'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
const ClickSpark = forwardRef(({ sparkColor = '#fff', sparkSize = 10, sparkRadius = 15, sparkCount = 8, duration = 400, easing = 'ease-out', extraScale = 1.0, lineWidth = 2, sparkOnClick = true, className, style, children }, ref) => {
    const canvasRef = useRef(null);
    const sparksRef = useRef([]);
    const rafRef = useRef(0);
    const sizeRef = useRef({ width: 0, height: 0, dpr: 1 });
    const allocate = (canvas) => {
        const { width, height, dpr } = sizeRef.current;
        const w = Math.round(width * dpr);
        const h = Math.round(height * dpr);
        if (canvas.width !== w)
            canvas.width = w;
        if (canvas.height !== h)
            canvas.height = h;
    };
    const release = (canvas) => {
        canvas.width = 0;
        canvas.height = 0;
    };
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const parent = canvas.parentElement;
        if (!parent)
            return;
        let resizeTimeout;
        const resizeCanvas = () => {
            const { width, height } = parent.getBoundingClientRect();
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const size = sizeRef.current;
            if (size.width !== width || size.height !== height || size.dpr !== dpr) {
                sizeRef.current = { width, height, dpr };
                if (sparksRef.current.length > 0)
                    allocate(canvas);
            }
        };
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(resizeCanvas, 100);
        };
        const ro = new ResizeObserver(handleResize);
        ro.observe(parent);
        resizeCanvas();
        return () => {
            ro.disconnect();
            clearTimeout(resizeTimeout);
        };
    }, []);
    const easeFunc = useCallback((t) => {
        switch (easing) {
            case 'linear':
                return t;
            case 'ease-in':
                return t * t;
            case 'ease-in-out':
                return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            default:
                return t * (2 - t);
        }
    }, [easing]);
    const draw = useCallback((timestamp) => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) {
            rafRef.current = 0;
            return;
        }
        const { dpr } = sizeRef.current;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
        sparksRef.current = sparksRef.current.filter((spark) => {
            const elapsed = timestamp - spark.startTime;
            if (elapsed >= duration) {
                return false;
            }
            const progress = Math.max(0, elapsed) / duration;
            const eased = easeFunc(progress);
            const distance = eased * sparkRadius * extraScale;
            const lineLength = sparkSize * (1 - eased);
            const x1 = spark.x + distance * Math.cos(spark.angle);
            const y1 = spark.y + distance * Math.sin(spark.angle);
            const x2 = spark.x + (distance + lineLength) * Math.cos(spark.angle);
            const y2 = spark.y + (distance + lineLength) * Math.sin(spark.angle);
            ctx.strokeStyle = spark.color;
            ctx.lineWidth = lineWidth;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
            return true;
        });
        if (sparksRef.current.length > 0) {
            rafRef.current = requestAnimationFrame(draw);
        }
        else {
            rafRef.current = 0;
            release(canvas);
        }
    }, [duration, easeFunc, extraScale, lineWidth, sparkRadius, sparkSize]);
    useEffect(() => {
        return () => {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = 0;
        };
    }, []);
    const spawn = useCallback((x, y) => {
        const colors = Array.isArray(sparkColor) && sparkColor.length > 0 ? sparkColor : [sparkColor];
        const now = performance.now();
        const newSparks = Array.from({ length: sparkCount }, (_, i) => ({
            x,
            y,
            angle: (2 * Math.PI * i) / sparkCount,
            startTime: now,
            color: colors[i % colors.length]
        }));
        sparksRef.current.push(...newSparks);
        const canvas = canvasRef.current;
        if (canvas)
            allocate(canvas);
        if (!rafRef.current)
            rafRef.current = requestAnimationFrame(draw);
    }, [draw, sparkColor, sparkCount]);
    useImperativeHandle(ref, () => ({
        burst: (x, y) => {
            const { width, height } = sizeRef.current;
            spawn(x ?? width / 2, y ?? height / 2);
        }
    }), [spawn]);
    const handleClick = (e) => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const rect = canvas.getBoundingClientRect();
        spawn(e.clientX - rect.left, e.clientY - rect.top);
    };
    return (_jsxs("div", { className: className, style: {
            width: '100%',
            height: '100%',
            position: 'relative',
            ...style
        }, onClick: sparkOnClick ? handleClick : undefined, children: [_jsx("canvas", { ref: canvasRef, style: {
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none'
                } }), children] }));
});
ClickSpark.displayName = 'ClickSpark';
export default ClickSpark;
